import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { Card, CardHeader } from '../ui/Card'
import { Button } from '../ui/Button'
import { Skeleton } from '../ui/Skeleton'
import { buildStorefrontUrl, getStorefrontUrlIssue } from '../../utils/buildStorefrontUrl'
import {
  computeOnboardingProgress,
  ONBOARDING_STEPS,
  type OnboardingStepDef,
} from '../../lib/onboardingChecklist'
import * as storeApi from '../../services/storeService'
import { fetchCategories } from '../../services/categoryService'
import { fetchProducts } from '../../services/productService'
import { fetchPickupLocations } from '../../services/commerceService'
import { getUserFacingErrorMessage } from '../../utils/userFacingError'

type OnboardingChecklistProps = {
  store: storeApi.StoreRecord
  onStoreUpdated: (store: storeApi.StoreRecord) => void
}

export function OnboardingChecklist({ store, onStoreUpdated }: OnboardingChecklistProps) {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [categoryTotal, setCategoryTotal] = useState(0)
  const [productTotal, setProductTotal] = useState(0)
  const [pickupCount, setPickupCount] = useState(0)
  const [dismissing, setDismissing] = useState(false)
  const [previewSaving, setPreviewSaving] = useState(false)

  const refreshMetrics = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const [categories, products, pickups] = await Promise.all([
        fetchCategories({ page: 1, limit: 1 }),
        fetchProducts({ page: 1, limit: 1 }),
        fetchPickupLocations(store.id),
      ])
      setCategoryTotal(categories.total ?? 0)
      setProductTotal(products.total ?? 0)
      setPickupCount((pickups.items ?? []).length)
    } catch (e) {
      setError(getUserFacingErrorMessage(e, 'Could not load setup progress'))
    } finally {
      setLoading(false)
    }
  }, [store.id])

  useEffect(() => {
    void refreshMetrics()
  }, [refreshMetrics])

  const progress = useMemo(
    () =>
      computeOnboardingProgress({
        store,
        categoryTotal,
        productTotal,
        pickupCount,
      }),
    [store, categoryTotal, productTotal, pickupCount],
  )

  const storefrontUrl = buildStorefrontUrl(store.subdomain)
  const storefrontUrlIssue = getStorefrontUrlIssue(store.subdomain)

  async function handlePreview() {
    if (!storefrontUrl) return
    setPreviewSaving(true)
    try {
      const updated = await storeApi.updateStore(store.id, {
        onboardingStorefrontPreviewedAt: new Date().toISOString(),
      })
      onStoreUpdated(updated)
      window.open(storefrontUrl, '_blank', 'noopener,noreferrer')
    } catch (e) {
      setError(getUserFacingErrorMessage(e, 'Could not save preview progress'))
    } finally {
      setPreviewSaving(false)
    }
  }

  async function handleDismiss() {
    if (!progress.allComplete) return
    setDismissing(true)
    setError(null)
    try {
      const updated = await storeApi.updateStore(store.id, {
        onboardingChecklistDismissedAt: new Date().toISOString(),
      })
      onStoreUpdated(updated)
    } catch (e) {
      setError(getUserFacingErrorMessage(e, 'Could not dismiss checklist'))
    } finally {
      setDismissing(false)
    }
  }

  function renderStepAction(step: OnboardingStepDef & { complete: boolean }) {
    if (step.id === 'previewStorefront') {
      return (
        <Button
          type="button"
          variant={step.complete ? 'secondary' : 'primary'}
          size="sm"
          disabled={!storefrontUrl || previewSaving}
          onClick={() => void handlePreview()}
        >
          {previewSaving ? 'Opening…' : step.complete ? 'Preview again' : 'Preview store'}
        </Button>
      )
    }
    if (!step.href) return null
    return (
      <Link to={step.href}>
        <Button type="button" variant={step.complete ? 'secondary' : 'primary'} size="sm">
          {step.complete ? 'Review' : 'Set up'}
        </Button>
      </Link>
    )
  }

  return (
    <Card className="overflow-hidden" aria-busy={loading}>
      <CardHeader
        title="Get your store ready"
        description="Complete these steps to go live. Progress is saved for this store."
        action={
          progress.allComplete ? (
            <Button type="button" variant="secondary" size="sm" disabled={dismissing} onClick={() => void handleDismiss()}>
              {dismissing ? 'Saving…' : 'Dismiss checklist'}
            </Button>
          ) : null
        }
      />
      {error ? <p className="mb-4 text-sm text-destructive">{error}</p> : null}
      {loading ? (
        <div className="space-y-3" aria-busy="true" aria-label="Loading setup checklist">
          {ONBOARDING_STEPS.map((step) => (
            <div key={step.id} className="flex gap-3 rounded-xl border border-border p-4">
              <Skeleton className="h-6 w-6 shrink-0 rounded-full" />
              <div className="min-w-0 flex-1 space-y-2">
                <Skeleton className="h-4 w-40" />
                <Skeleton className="h-3 w-full max-w-md" />
              </div>
            </div>
          ))}
        </div>
      ) : (
        <>
          <div className="mb-4 flex items-center justify-between gap-3 text-sm">
            <p className="text-muted-foreground">
              <span className="font-medium text-foreground">{progress.completedRequiredCount}</span> of{' '}
              {progress.requiredTotalCount} complete
            </p>
            <div className="h-2 min-w-[8rem] flex-1 max-w-xs overflow-hidden rounded-full bg-muted">
              <div
                className="h-full rounded-full bg-primary transition-all duration-300"
                style={{
                  width: `${
                    progress.requiredTotalCount
                      ? (progress.completedRequiredCount / progress.requiredTotalCount) * 100
                      : 0
                  }%`,
                }}
              />
            </div>
          </div>
          <ul className="space-y-3">
            {progress.steps.map((step) => (
              <li
                key={step.id}
                className="flex flex-col gap-3 rounded-xl border border-border bg-card/50 p-4 sm:flex-row sm:items-center sm:justify-between"
              >
                <div className="flex min-w-0 gap-3">
                  <span
                    className={`mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-bold ${
                      step.complete
                        ? 'bg-primary text-primary-foreground'
                        : 'border border-border bg-muted text-muted-foreground'
                    }`}
                    aria-hidden
                  >
                    {step.complete ? '✓' : ''}
                  </span>
                  <div className="min-w-0">
                    <p className="font-medium text-foreground">
                      {step.title}
                      {step.optional ? (
                        <span className="ml-2 text-xs font-normal text-muted-foreground">(optional)</span>
                      ) : null}
                    </p>
                    <p className="mt-0.5 text-sm text-muted-foreground">{step.description}</p>
                    {!storefrontUrl && step.id === 'previewStorefront' ? (
                      <p className="mt-1 text-xs text-amber-700 dark:text-amber-300">
                        {storefrontUrlIssue === 'missing_subdomain'
                          ? 'This store has no subdomain yet — set one in store settings before previewing.'
                          : `Set VITE_PUBLIC_STOREFRONT_URL to your storefront base URL. The link uses this store’s subdomain (${store.subdomain || '—'}) from the API.`}
                      </p>
                    ) : null}
                  </div>
                </div>
                <div className="shrink-0 sm:pl-2">{renderStepAction(step)}</div>
              </li>
            ))}
          </ul>
          {storefrontUrl && progress.allComplete ? (
            <div className="mt-4 flex flex-wrap gap-2 border-t border-border pt-4">
              <Button type="button" onClick={() => void handlePreview()} disabled={previewSaving}>
                {previewSaving ? 'Opening…' : 'Go live — preview storefront'}
              </Button>
            </div>
          ) : null}
        </>
      )}
    </Card>
  )
}
